import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Alert,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useOrganization } from '../../context/OrganizationContext';
import { useOrganizationAccess } from '../../hooks/useOrganizationAccess';
import OrganizationLayout from '../../components/organization/OrganizationLayout';
import styles from '../../styles/RolesPermissions.module.css';

/**
 * RolesPermissions Page
 *
 * Displays comprehensive role and permission information:
 * - Role descriptions and hierarchy
 * - Detailed permission matrix
 * - Access control documentation
 *
 * Access: Owner and Admin roles only
 */
function RolesPermissions() {
  const { userRole } = useOrganization();
  const { hasWriteAccess } = useOrganizationAccess();
  const [currentTab, setCurrentTab] = useState(0);

  // Check if user has permission to view this page
  const canView = userRole === 'owner' || userRole === 'admin';

  // Role definitions with descriptions
  const roles = [
    {
      name: 'owner',
      displayName: 'Owner',
      color: '#d32f2f',
      description: 'Full control over the organization including billing, settings, and flight operations. Can perform all actions.',
      permissions: [
        'All Admin permissions',
        'Perform flight operations',
        'Manage billing and subscription',
        'Delete organization',
        'Transfer ownership',
        'View all financial data'
      ]
    },
    {
      name: 'admin',
      displayName: 'Admin',
      color: '#f57c00',
      description: 'Can manage team members, organization settings, and perform flight operations. Cannot access billing.',
      permissions: [
        'All Manager permissions',
        'Perform flight operations',
        'Manage team members (invite, remove)',
        'Assign roles (except Owner)',
        'Edit organization settings',
        'View all activity logs'
      ]
    },
    {
      name: 'manager',
      displayName: 'Manager',
      color: '#1976d2',
      description: 'Can perform flight operations, manage fleet, and access analytics. Has full operational control.',
      permissions: [
        'All Pilot permissions',
        'Perform flight operations',
        'Manage fleet (drones, batteries, payloads)',
        'View all operations and flight logs',
        'Invite team members',
        'Access analytics and reports',
        'Manage compliance (licenses, checklists, incidents)'
      ]
    },
    {
      name: 'pilot',
      displayName: 'Pilot',
      color: '#388e3c',
      description: 'Can perform flight operations and manage fleet. Limited to own operations data.',
      permissions: [
        'Create and manage own operations',
        'Submit LAANC authorizations',
        'Add/edit drones, batteries, payloads',
        'Log flight hours and maintenance',
        'Create/complete pre-flight checklists',
        'Report incidents (own only)'
      ]
    },
    {
      name: 'viewer',
      displayName: 'Viewer',
      color: '#757575',
      description: 'Read-only access to organization data. Cannot create or modify any records.',
      permissions: [
        'View all operations',
        'View all flight logs',
        'View fleet inventory',
        'View pilot licenses',
        'View completed checklists and incidents',
        'View team members'
      ]
    }
  ];

  // Comprehensive permission matrix
  const permissionMatrix = {
    'Dashboard': {
      owner: true,
      admin: true,
      manager: true,
      pilot: false,
      viewer: true
    },
    'Operations & LAANC': {
      'Create Operation': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: false
      },
      'View All Operations': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: true
      },
      'Submit LAANC Authorization': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: false
      },
      'Cancel/Delete/Rescind Auth': {
        owner: 'Own ops only',
        admin: 'Own ops only',
        manager: 'Own ops only',
        pilot: 'Own ops only',
        viewer: 'N/A'
      },
      'View Operation Templates': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: true
      },
      'Create Operation Templates': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: false
      },
      'View Calendar': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: true
      },
      'Create / Edit Calendar Events': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: false
      },
      
    },
    'File Operations': {
      'View Files (All Ops)': {
        owner: true,
        admin: true,
        manager: true,
        pilot: 'Own ops only',
        viewer: true
      },
      'Upload Files': {
        owner: 'Own ops only',
        admin: 'Own ops only',
        manager: 'Own ops only',
        pilot: 'Own ops only',
        viewer: 'N/A'
      },
      'Delete Files': {
        owner: 'Own ops only',
        admin: 'Own ops only',
        manager: 'Own ops only',
        pilot: 'Own ops only',
        viewer: 'N/A'
      },
      'Share Files': {
        owner: 'Own ops only*',
        admin: 'Own ops only*',
        manager: 'Own ops only*',
        pilot: 'Own ops only*',
        viewer: 'N/A'
      },
      'Revoke Shared Access': {
        owner: 'Own ops only',
        admin: 'Own ops only',
        manager: 'Own ops only',
        pilot: 'Own ops only',
        viewer: 'N/A'
      },
      'Download Files': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: true
      }
    },
    'Fleet Management': {
      'View Fleet': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: true
      },
      'Add/Edit Drones': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: false
      },
      'Delete Drones': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: false
      },
      'Add/Edit Batteries/Payloads': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: false
      },
      'Delete Batteries/Payloads': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: false
      },
      'View Maintenance Logs': {
        owner: true,
        admin: true,
        manager: true,
        pilot: 'Own drones only',
        viewer: false
      },
      'Add/Edit Maintenance Logs': {
        owner: true,
        admin: true,
        manager: true,
        pilot: 'Own drones only',
        viewer: false
      },
      'Delete Maintenance Logs': {
        owner: true,
        admin: true,
        manager: false,
        pilot: false,
        viewer: false
      }
    },
    'Flight Logs': {
      'View All Logs': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: true
      },
      'Log Flight Hours': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: false
      },
      'Edit All Logs': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: false
      },
      'Delete All Logs': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: false
      }
    },
    'Compliance': {
      'View Compliance Dashboard': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: false
      },
      'View Pilot Licenses': {
        owner: 'All licenses',
        admin: 'All licenses',
        manager: 'All licenses',
        pilot: 'Own only',
        viewer: 'All licenses'
      },
      'Add Pilot License': {
        owner: 'Any team member',
        admin: 'Any team member',
        manager: 'Any team member',
        pilot: 'Own only (1 max)',
        viewer: false
      },
      'Edit Pilot License': {
        owner: 'Any license',
        admin: 'Any license',
        manager: 'Any license',
        pilot: 'Own only',
        viewer: false
      },
      'Delete Pilot License': {
        owner: true,
        admin: true,
        manager: false,
        pilot: false,
        viewer: false
      },
      'View Checklist Templates': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: false
      },
      'Create/Edit Checklist Templates': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: false
      },
      'Complete Checklists': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: false
      },
      'View All Completed Checklists': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: true
      },
      'Export Checklist to PDF': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: true
      },
      'Create Incident Reports': {
        owner: true,
        admin: true,
        manager: true,
        pilot: true,
        viewer: false
      },
      'View All Incidents': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: true
      },
      'Update Incident Status': {
        owner: true,
        admin: true,
        manager: true,
        pilot: 'Own incidents only',
        viewer: false
      }
    },
    'Analytics & Reports': {
      'Flight Analytics': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: true
      },
      'Utilization Reports': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: true
      },
      'Cost Analysis': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: true
      },
      'Flight Logs (Analytics)': {
        owner: true,
        admin: true,
        manager: true,
        pilot: 'Own logs only',
        viewer: true
      }
    },
    'Team Management': {
      'View Team Members': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: true
      },
      'Invite Members': {
        owner: true,
        admin: true,
        manager: true,
        pilot: false,
        viewer: false
      },
      'Edit Roles': {
        owner: true,
        admin: true,
        manager: false,
        pilot: false,
        viewer: false
      },
      'Remove Members': {
        owner: true,
        admin: true,
        manager: false,
        pilot: false,
        viewer: false
      }
    },
    'Organization Settings': {
      'Edit Organization Profile': {
        owner: true,
        admin: true,
        manager: false,
        pilot: false,
        viewer: false
      },
      'View Billing & Subscription': {
        owner: true,
        admin: 'View only',
        manager: false,
        pilot: false,
        viewer: false
      },
      'Manage Billing & Subscription': {
        owner: true,
        admin: false,
        manager: false,
        pilot: false,
        viewer: false
      },
      'Integrations': {
        owner: true,
        admin: false,
        manager: false,
        pilot: false,
        viewer: false
      },
      'View Roles & Permissions': {
        owner: true,
        admin: true,
        manager: false,
        pilot: false,
        viewer: false
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const renderPermissionIcon = (permission) => {
    if (permission === true) {
      return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />;
    } else if (permission === false) {
      return <CancelIcon sx={{ color: '#f44336', fontSize: 20 }} />;
    } else {
      return <Chip label={permission} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />;
    }
  };

  const renderPermissionTable = (category, permissions) => {
    // Check if permissions is a simple boolean map (like Dashboard or Analytics)
    const isSimplePermission = typeof Object.values(permissions)[0] === 'boolean';

    if (isSimplePermission) {
      return (
        <TableContainer component={Paper} sx={{ mb: 4 }} elevation={2}>
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, width: '30%' }}>{category}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, width: '14%' }}>Owner</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, width: '14%' }}>Admin</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, width: '14%' }}>Manager</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, width: '14%' }}>Pilot</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, width: '14%' }}>Viewer</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Access {category}</TableCell>
                <TableCell align="center">{renderPermissionIcon(permissions.owner)}</TableCell>
                <TableCell align="center">{renderPermissionIcon(permissions.admin)}</TableCell>
                <TableCell align="center">{renderPermissionIcon(permissions.manager)}</TableCell>
                <TableCell align="center">{renderPermissionIcon(permissions.pilot)}</TableCell>
                <TableCell align="center">{renderPermissionIcon(permissions.viewer)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    // Complex permission structure with sub-permissions
    return (
      <TableContainer component={Paper} sx={{ mb: 4 }} elevation={2}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, width: '30%' }}>{category}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, width: '14%' }}>Owner</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, width: '14%' }}>Admin</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, width: '14%' }}>Manager</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, width: '14%' }}>Pilot</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, width: '14%' }}>Viewer</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(permissions).map(([permission, rolePermissions]) => (
              <TableRow key={permission} hover>
                <TableCell sx={{ wordWrap: 'break-word' }}>{permission}</TableCell>
                <TableCell align="center">{renderPermissionIcon(rolePermissions.owner)}</TableCell>
                <TableCell align="center">{renderPermissionIcon(rolePermissions.admin)}</TableCell>
                <TableCell align="center">{renderPermissionIcon(rolePermissions.manager)}</TableCell>
                <TableCell align="center">{renderPermissionIcon(rolePermissions.pilot)}</TableCell>
                <TableCell align="center">{renderPermissionIcon(rolePermissions.viewer)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (!canView) {
    return (
      <OrganizationLayout>
        <Box sx={{ p: 4 }}>
          <Alert severity="error">
            You do not have permission to view this page. Only Owners and Admins can access role and permission settings.
          </Alert>
        </Box>
      </OrganizationLayout>
    );
  }

  return (
    <OrganizationLayout>
      <Box className={styles.container}>
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AdminPanelSettingsIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Typography variant="h4" component="h1">
              Roles & Permissions
            </Typography>
          </Box>
          <Typography variant="body1" color="textSecondary">
            Understand role hierarchy and access control across your organization.
          </Typography>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          Roles define what members can do within your organization. Permissions are automatically assigned based on roles and cannot be customized individually.
        </Alert>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="Role Overview" />
            <Tab label="Permission Matrix" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {currentTab === 0 && (
          <Box>
            {/* Role Cards */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Role Hierarchy
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Roles are ordered from highest to lowest privilege. Each role inherits permissions from roles below it.
            </Typography>

            {roles.map((role, index) => (
              <Card key={role.name} elevation={2} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Chip
                      label={role.displayName}
                      sx={{
                        backgroundColor: role.color,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                    />
                    {index === 0 && (
                      <Chip label="Highest Privilege" size="small" variant="outlined" />
                    )}
                    {index === roles.length - 1 && (
                      <Chip label="Read-Only" size="small" variant="outlined" />
                    )}
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {role.description}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Key Permissions:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                    {role.permissions.map((permission, idx) => (
                      <Typography component="li" variant="body2" key={idx} sx={{ mb: 0.5 }}>
                        {permission}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {currentTab === 1 && (
          <Box>
            {/* Permission Matrix */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Detailed Permission Matrix
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              This table shows exactly what each role can do across all features. Green checkmarks indicate full access, red X marks indicate no access.
            </Typography>

            {Object.entries(permissionMatrix).map(([category, permissions]) =>
              renderPermissionTable(category, permissions)
            )}

            {/* Legend */}
            <Card sx={{ mt: 4, backgroundColor: '#f9f9f9' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Legend
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                    <Typography variant="body2">Full Access</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CancelIcon sx={{ color: '#f44336', fontSize: 20 }} />
                    <Typography variant="body2">No Access</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Own only" size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                    <Typography variant="body2">Limited to own data</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Own ops only" size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                    <Typography variant="body2">Limited to own operations</Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  <strong>Important Notes:</strong>
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div" sx={{ mt: 1 }}>
                  • <strong>LAANC Authorizations:</strong> Only the operation creator can cancel, delete, or rescind authorizations (regulatory compliance requirement). However, viewers cannot modify authorizations under any circumstances, even if they created the operation while in a different role.
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div">
                  • <strong>File Operations:</strong> Upload, delete, and share are restricted to the operation creator only
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div">
                  • <strong>File Sharing (*):</strong> Requires subscription plan with file sharing feature enabled
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div">
                  • <strong>Pilot Licenses:</strong> Pilots can view and edit only their own license (max 1). Managers/Admins/Owners can add licenses for any team member. Only Owners and Admins can delete licenses.
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div">
                  • <strong>Auto-Filtering:</strong> Pilots automatically see only their own operations in the operations list
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </OrganizationLayout>
  );
}

export default RolesPermissions;
